import { computed, inject } from "@angular/core";
import { UserStoreService } from "../../../services/store/user/user.store.service";
import { CalculatedBalances } from "@bitwarden/web-vault/app/services/store/calculated-balance.collection.abstraction";
import { ScenarioStoreService } from "@bitwarden/web-vault/app/services/store/scenario/scenario.store.service";
import { ScenarioPaths, ScenarioTypes } from "@bitwarden/web-vault/app/models/enum/scenario.types";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { SignalSubscriptionImpl } from "@bitwarden/web-vault/app/components/tables/data-subscription";
import { BalanceDatesUtils } from "@bitwarden/web-vault/app/services/store/calculation-base/balances/balanceDates.utils";
import { getName } from "@bitwarden/web-vault/app/shared/utils/helper-string";
import type { TableSource } from "./transaction-table";
import { sortTransactionViews } from "@bitwarden/web-vault/app/shared/utils/helper.transactionView/sort";

export class ScenarioTransactionTableSubscription extends SignalSubscriptionImpl<TableSource[]> {
  userStoreService = inject(UserStoreService);
  scenarioStoreService = inject(ScenarioStoreService);
  balancesByTransaction: CalculatedBalances = new Map<string, number>();

  private selectedPathIndex = computed(() => {
    const selectedPath = this.scenarioStoreService.currentScenarioType();
    return ScenarioPaths?.[selectedPath] ?? 0;
  });

  private _finalBalanceAmountList = computed(() => {
    const scenarioBalanceByTime = this.scenarioStoreService.balanceByTime.balances();
    if (!scenarioBalanceByTime) {
      return [];
    }
    const endDate = this.scenarioStoreService.period.selected().endDate;
    const endTimeStamp = BalanceDatesUtils.getEndOfDayTimeStamp(endDate);
    return scenarioBalanceByTime.map((balances) => balances.get(endTimeStamp.toString()));
  });

  get finalBalanceAmountList(): number[] {
    return this._finalBalanceAmountList();
  }

  get source() {
    const accountViews = this.userStoreService.accounts.accountViews();
    const institutionViews = this.userStoreService.institutions.institutionViews();
    const scenarioDisplayViews = this.scenarioStoreService.interestScenario.collection();
    const scenarioCurrBalance = this.scenarioStoreService.balanceByTransaction.balances();
    const scenarioType = this.scenarioStoreService.currentScenarioType();

    if (!scenarioDisplayViews) {
      return [];
    }

    const currentScenarioDisplayData = scenarioDisplayViews.find(
      (data) => scenarioType == data.scenarioType,
    );

    const transactions = currentScenarioDisplayData?.fullTransactionList;
    let accountName = "";
    if (scenarioType === ScenarioTypes.BestNewInterestRate) {
      accountName = currentScenarioDisplayData?.createdRecords.accounts[0]?.name;
    }

    if (scenarioCurrBalance) {
      this.balancesByTransaction =
        scenarioCurrBalance?.[this.selectedPathIndex()] || new Map<string, number>();
    }

    return transactions
      ?.sort((a, b) => sortTransactionViews(b, a))
      .map((transaction: TransactionView) => {
        const accountId = transaction.accountLink.id;
        const accountView = accountViews.find((acc) => acc.id === accountId);

        return {
          account: accountName || getName(accountViews, transaction.accountLink.id),
          institution: getName(institutionViews, transaction.c_institutionLink.id),
          origin: transaction.origin,
          symbol: transaction.c_valuation.normalizedValue.symbol,
          amount: transaction.c_valuation.normalizedValue.amount,
          balance: this.balancesByTransaction.get(transaction.id) || 0,
          accountView,
          transactionDate: transaction.postDate,
          description: transaction.description,
        };
      });
  }
}
